import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type { UserDto } from '@/features/admin-users/endpoints';
import { platformUsersEndpoints } from '@/features/platform-users/endpoints';
import type { UserPickerOption } from '@/components/ui/user-picker';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

const SEARCH_MIN_LENGTH = 1;
const SEARCH_DEBOUNCE_MS = 200;
const SEARCH_DEFAULT_LIMIT = 25;

export interface UsePlatformUserSearchResult {
  readonly results: readonly UserPickerOption[];
  readonly isLoading: boolean;
  readonly error: Error | null;
}

/**
 * Live GLOBAL (platform-plane) user search for the federated-identity link picker (PTV2-1451). Mirrors
 * {@code useUserSearch} but hits {@code GET /platform/users/search} — which always targets the global
 * plane regardless of any active-tenant selection — so an admin links a captured identity to any global
 * user. Debounced, min-length gated, returns pre-mapped picker options.
 */
export function usePlatformUserSearch(
  rawQuery: string,
  options: { debounceMs?: number } = {},
): UsePlatformUserSearchResult {
  const debounceMs = options.debounceMs ?? SEARCH_DEBOUNCE_MS;

  const trimmed = rawQuery.trim();
  const debouncedQuery = useDebouncedValue(trimmed, debounceMs);
  const isActive = debouncedQuery.length >= SEARCH_MIN_LENGTH;

  const query = useQuery<readonly UserPickerOption[]>({
    queryKey: ['platform', 'users', 'search', debouncedQuery] as const,
    queryFn: async ({ signal }) => {
      const page = await platformUsersEndpoints.searchUsers(
        { q: debouncedQuery, page: 0, size: SEARCH_DEFAULT_LIMIT },
        { signal },
      );
      return page.content.map(toPickerOption);
    },
    enabled: isActive,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const debounceSettling = trimmed !== debouncedQuery;
  const isLoading = isActive && (debounceSettling || query.isFetching);

  return {
    results: isActive ? (query.data ?? []) : [],
    isLoading,
    error: isActive ? (query.error ?? null) : null,
  };
}

function toPickerOption(user: UserDto): UserPickerOption {
  const primary =
    user.fullName !== undefined && user.fullName.trim().length > 0
      ? user.fullName
      : (user.name ?? user.email ?? '');
  return {
    guid: user.uuid ?? '',
    fullName: primary,
    subtitle: user.email ?? null,
  };
}
